import React, { useState } from 'react';
import { Phone, Volume2, Play, Pause, CheckCircle, XCircle, Loader2, ShieldCheck } from 'lucide-react';
import { voiceAgentAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const CallConfigTab = ({ 
  agent, 
  setAgent, 
  agentId,
  hasFeature,
  assignPhoneNumber,
  unassignPhoneNumber
}) => {
  const { clientUser } = useAuth();
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // { valid, error, phone_number }

  const handleVerifyCallerId = async () => {
    const num = agent.outboundCallerId?.trim();
    if (!num) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const response = await voiceAgentAPI.validateCallerId(clientUser?.id, num);
      setVerifyResult(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Verification failed';
      setVerifyResult({ valid: false, error: detail });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Inbound Phone Number Assignment */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-2">Inbound Phone Number</h2>
        <p className="text-gray-500 text-sm mb-6">Assign a Telnyx phone number to receive inbound calls handled by this AI agent</p>
        
        {agent.assignedPhoneNumber ? (
          <div className="bg-[#1a1a1f] border border-teal-500/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-500/20 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5 text-teal-400" />
                </div>
                <div>
                  <p className="text-white font-medium">{agent.assignedPhoneNumber}</p>
                  <p className="text-teal-400 text-sm">Active - Receiving inbound calls</p>
                </div>
              </div>
              <button
                onClick={unassignPhoneNumber}
                className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
              >
                Unassign
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-4">No phone number assigned. Enter a Telnyx phone number to enable inbound calls.</p>
              <div className="flex gap-3">
                <input
                  type="tel"
                  placeholder="+1 507 666 0332"
                  className="flex-1 bg-[#0d1117] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  id="phone-assign-input"
                  data-testid="phone-assign-input"
                />
                <button
                  onClick={() => {
                    const input = document.getElementById('phone-assign-input');
                    if (input.value) {
                      assignPhoneNumber(input.value);
                    }
                  }}
                  className="px-6 py-2.5 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium"
                  data-testid="assign-phone-btn"
                >
                  Assign
                </button>
              </div>
            </div>
            <p className="text-gray-500 text-xs">
              Available Telnyx numbers: +15076660332, +13397771605, +12694009001
            </p>
          </div>
        )}
      </section>

      {/* Outbound Caller ID */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-2">Outbound Caller ID</h2>
        <p className="text-gray-500 text-sm mb-6">Set the phone number displayed when this agent makes outbound calls or campaigns</p>
        
        <div className="bg-[#1a1a1f] border border-gray-700 rounded-lg p-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-gray-400 text-xs mb-1.5">Caller ID Number (E.164 format)</label>
              <input
                type="tel"
                value={agent.outboundCallerId || ''}
                onChange={(e) => {
                  setAgent(prev => ({ ...prev, outboundCallerId: e.target.value }));
                  setVerifyResult(null);
                }}
                placeholder="+12025551234"
                className="w-full bg-[#0d1117] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400 placeholder-gray-600"
                data-testid="outbound-caller-id-input"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleVerifyCallerId}
                disabled={verifying || !agent.outboundCallerId?.trim()}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                data-testid="verify-caller-id-btn"
              >
                {verifying ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Checking...</>
                ) : (
                  <><ShieldCheck className="w-4 h-4" /> Verify</>
                )}
              </button>
            </div>
          </div>

          {/* Verification Result */}
          {verifyResult && (
            <div className={`mt-3 flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
              verifyResult.valid 
                ? 'bg-green-500/10 border border-green-500/30' 
                : 'bg-red-500/10 border border-red-500/30'
            }`}>
              {verifyResult.valid ? (
                <><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" /><span className="text-green-400">Verified — {verifyResult.phone_number} is provisioned on Telnyx (status: {verifyResult.status})</span></>
              ) : (
                <><XCircle className="w-4 h-4 text-red-400 flex-shrink-0" /><span className="text-red-400">{verifyResult.error}</span></>
              )}
            </div>
          )}

          {/* Current status indicator */}
          {!verifyResult && agent.outboundCallerId && agent.outboundCallerIdVerified && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-teal-400" />
              <span className="text-teal-400">Outbound calls will show: {agent.outboundCallerId}</span>
            </div>
          )}

          <p className="text-gray-600 text-xs mt-3">
            Must be a verified Telnyx number on your account. Click &quot;Verify&quot; to check before saving.
          </p>
        </div>
      </section>

      {/* Call Configuration */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-6">Call Configuration</h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Max Call Duration (seconds)</label>
            <input
              type="number"
              value={agent.maxCallDuration}
              onChange={(e) => setAgent(prev => ({ ...prev, maxCallDuration: parseInt(e.target.value) }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-white">Record Calls</span>
              <p className="text-gray-500 text-sm">Save call recordings for review</p>
            </div>
            {hasFeature('voice_recordings') ? (
              <div 
                className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${agent.recordCalls ? 'bg-teal-400' : 'bg-gray-700'}`}
                onClick={() => setAgent(prev => ({ ...prev, recordCalls: !prev.recordCalls }))}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${agent.recordCalls ? 'translate-x-5' : ''}`} />
              </div>
            ) : (
              <span className="text-xs text-yellow-500 bg-yellow-500/10 px-2 py-1 rounded">Disabled by admin</span>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-white">Enable Call Transfer</span>
              <p className="text-gray-500 text-sm">Allow transferring calls to a human agent</p>
            </div>
            {hasFeature('call_transfer') ? (
              <div 
                className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${agent.transferEnabled ? 'bg-teal-400' : 'bg-gray-700'}`}
                onClick={() => setAgent(prev => ({ ...prev, transferEnabled: !prev.transferEnabled }))}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${agent.transferEnabled ? 'translate-x-5' : ''}`} />
              </div>
            ) : (
              <span className="text-xs text-yellow-500 bg-yellow-500/10 px-2 py-1 rounded">Disabled by admin</span>
            )}
          </div>

          {agent.transferEnabled && (
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 text-sm mb-2">Transfer Phone Number *</label>
                <input
                  type="tel"
                  value={agent.transferNumber}
                  onChange={(e) => setAgent(prev => ({ ...prev, transferNumber: e.target.value }))}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="+1 555 123 4567"
                />
                <p className="text-gray-500 text-xs mt-1">The phone number where calls will be transferred</p>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm mb-2">Transfer Message</label>
                <input
                  type="text"
                  value={agent.transferMessage}
                  onChange={(e) => setAgent(prev => ({ ...prev, transferMessage: e.target.value }))}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="I'll transfer you to a live agent now. Please hold."
                />
                <p className="text-gray-500 text-xs mt-1">Message played before transferring</p>
              </div>
              
              <div>
                <label className="block text-gray-300 text-sm mb-2">Transfer Trigger Phrases</label>
                <textarea
                  value={agent.transferTriggerPhrases?.join(', ') || ''}
                  onChange={(e) => setAgent(prev => ({ 
                    ...prev, 
                    transferTriggerPhrases: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                  }))}
                  className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                  placeholder="speak to a human, talk to someone, live agent"
                  rows={2}
                />
                <p className="text-gray-500 text-xs mt-1">Comma-separated phrases that trigger transfer to live agent</p>
              </div>

              {/* Custom API Transfer */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                <div>
                  <span className="text-white">Custom API Transfer</span>
                  <p className="text-gray-500 text-sm">Use custom API endpoint for call routing</p>
                </div>
                <div 
                  className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${agent.customApiTransfer?.enabled ? 'bg-teal-400' : 'bg-gray-700'}`}
                  onClick={() => setAgent(prev => ({ 
                    ...prev, 
                    customApiTransfer: { 
                      ...prev.customApiTransfer, 
                      enabled: !prev.customApiTransfer?.enabled 
                    }
                  }))}
                >
                  <div className={`w-5 h-5 rounded-full bg-white transition-transform ${agent.customApiTransfer?.enabled ? 'translate-x-5' : ''}`} />
                </div>
              </div>

              {agent.customApiTransfer?.enabled && (
                <div>
                  <label className="block text-gray-300 text-sm mb-2">API Endpoint URL</label>
                  <input
                    type="url"
                    value={agent.customApiTransfer?.endpoint || ''}
                    onChange={(e) => setAgent(prev => ({ 
                      ...prev, 
                      customApiTransfer: { ...prev.customApiTransfer, endpoint: e.target.value }
                    }))}
                    className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                    placeholder="https://api.example.com/transfer"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Silence Handling Section */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-2">Silence Handling</h2>
        <p className="text-gray-500 text-sm mb-6">Configure how the AI responds when the caller goes silent</p>
        
        <div className="space-y-6">
          <div>
            <label className="block text-gray-300 text-sm mb-2">User Idle Threshold (seconds)</label>
            <input
              type="number"
              value={agent.silenceHandling?.userIdleThreshold || 7}
              onChange={(e) => setAgent(prev => ({ 
                ...prev, 
                silenceHandling: { ...prev.silenceHandling, userIdleThreshold: parseInt(e.target.value) }
              }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
              min={1}
              max={30}
            />
            <p className="text-gray-500 text-xs mt-1">Time to wait before playing idle message</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-gray-300 text-sm">First Idle Message</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-xs">Dynamic</span>
                <div 
                  className={`w-10 h-6 rounded-full p-1 cursor-pointer transition-colors ${agent.silenceHandling?.firstIdleMessageDynamic ? 'bg-teal-400' : 'bg-gray-700'}`}
                  onClick={() => setAgent(prev => ({ 
                    ...prev, 
                    silenceHandling: { ...prev.silenceHandling, firstIdleMessageDynamic: !prev.silenceHandling?.firstIdleMessageDynamic }
                  }))}
                >
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${agent.silenceHandling?.firstIdleMessageDynamic ? 'translate-x-4' : ''}`} />
                </div>
              </div>
            </div>
            <input
              type="text"
              value={agent.silenceHandling?.firstIdleMessage || ''}
              onChange={(e) => setAgent(prev => ({ 
                ...prev, 
                silenceHandling: { ...prev.silenceHandling, firstIdleMessage: e.target.value }
              }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
              placeholder="Are you still there?"
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Second Idle Message</label>
            <input
              type="text"
              value={agent.silenceHandling?.secondIdleMessage || ''}
              onChange={(e) => setAgent(prev => ({ 
                ...prev, 
                silenceHandling: { ...prev.silenceHandling, secondIdleMessage: e.target.value }
              }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
              placeholder="Would you like to continue our conversation?"
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Last Idle Message (before ending)</label>
            <input
              type="text"
              value={agent.silenceHandling?.lastIdleMessage || ''}
              onChange={(e) => setAgent(prev => ({ 
                ...prev, 
                silenceHandling: { ...prev.silenceHandling, lastIdleMessage: e.target.value }
              }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
              placeholder="I'll leave you for now. Have a nice day!"
            />
          </div>
        </div>
      </section>

      {/* End Call Rules Section */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-6">End Call Rules</h2>
        
        <div className="space-y-6">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Max Call Duration (seconds)</label>
            <input
              type="number"
              value={agent.endCallRules?.maxCallDuration || agent.maxCallDuration || 600}
              onChange={(e) => setAgent(prev => ({ 
                ...prev, 
                endCallRules: { ...prev.endCallRules, maxCallDuration: parseInt(e.target.value) },
                maxCallDuration: parseInt(e.target.value)
              }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className="text-white">Enable Automatic Call Ending</span>
              <p className="text-gray-500 text-sm">Automatically end call based on AI detection</p>
            </div>
            <div 
              className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${agent.endCallRules?.enableAutomaticEnding ? 'bg-teal-400' : 'bg-gray-700'}`}
              onClick={() => setAgent(prev => ({ 
                ...prev, 
                endCallRules: { ...prev.endCallRules, enableAutomaticEnding: !prev.endCallRules?.enableAutomaticEnding }
              }))}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${agent.endCallRules?.enableAutomaticEnding ? 'translate-x-5' : ''}`} />
            </div>
          </div>

          {agent.endCallRules?.enableAutomaticEnding && (
            <div>
              <label className="block text-gray-300 text-sm mb-2">Automatic Ending Message</label>
              <input
                type="text"
                value={agent.endCallRules?.automaticEndingMessage || ''}
                onChange={(e) => setAgent(prev => ({ 
                  ...prev, 
                  endCallRules: { ...prev.endCallRules, automaticEndingMessage: e.target.value }
                }))}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                placeholder="Thank you for calling. Goodbye!"
              />
            </div>
          )}
        </div>
      </section>

      {/* Response Behavior Section */}
      <section className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-6">Response Behavior</h2>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-white">Filler Phrases</span>
              <p className="text-gray-500 text-sm">Words or phrases spoken while your assistant is generating a response</p>
            </div>
            <div 
              className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${agent.responseBehavior?.fillerPhrasesEnabled ? 'bg-teal-400' : 'bg-gray-700'}`}
              onClick={() => setAgent(prev => ({ 
                ...prev, 
                responseBehavior: { ...prev.responseBehavior, fillerPhrasesEnabled: !prev.responseBehavior?.fillerPhrasesEnabled }
              }))}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${agent.responseBehavior?.fillerPhrasesEnabled ? 'translate-x-5' : ''}`} />
            </div>
          </div>

          {agent.responseBehavior?.fillerPhrasesEnabled && (
            <div>
              <label className="block text-gray-300 text-sm mb-2">Filler Phrases (comma-separated)</label>
              <textarea
                value={agent.responseBehavior?.fillerPhrases?.join(', ') || ''}
                onChange={(e) => setAgent(prev => ({ 
                  ...prev, 
                  responseBehavior: { 
                    ...prev.responseBehavior, 
                    fillerPhrases: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                  }
                }))}
                className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
                placeholder="Let me check that for you..., One moment please..., Let me see..."
                rows={2}
              />
            </div>
          )}

          <div>
            <label className="block text-gray-300 text-sm mb-2">Personality</label>
            <select
              value={agent.responseBehavior?.personality || 'friendly'}
              onChange={(e) => setAgent(prev => ({ 
                ...prev, 
                responseBehavior: { ...prev.responseBehavior, personality: e.target.value }
              }))}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-white outline-none focus:border-teal-400"
            >
              <option value="friendly">Friendly & Helpful</option>
              <option value="professional">Professional & Formal</option>
              <option value="casual">Casual & Relaxed</option>
              <option value="empathetic">Empathetic & Caring</option>
              <option value="enthusiastic">Enthusiastic & Energetic</option>
              <option value="calm">Calm & Reassuring</option>
              <option value="authoritative">Authoritative & Expert</option>
            </select>
            <p className="text-gray-500 text-xs mt-1">Sets the overall tone of your AI assistant</p>
          </div>
        </div>
      </section>

      {/* Ambient Sound Section */}
      <AmbientSoundSection agent={agent} setAgent={setAgent} />
    </div>
  );
};

// Ambient Sound Component
const AmbientSoundSection = ({ agent, setAgent }) => {
  const [playingAudio, setPlayingAudio] = useState(null);
  
  const backgroundAudioOptions = [
    { id: 'call_center', name: 'Call Center Background', description: 'call_center' },
    { id: 'city_traffic', name: 'City Traffic Outdoor', description: 'filter' },
    { id: 'restaurant', name: 'Restaurant', description: 'restaurant' },
    { id: 'office', name: 'Office Ambience', description: 'office' },
    { id: 'office_1', name: 'Office Ambience New', description: 'office_1' },
  ];

  const toggleAudioPreview = (audioId) => {
    if (playingAudio === audioId) {
      setPlayingAudio(null);
    } else {
      setPlayingAudio(audioId);
    }
  };

  return (
    <section className="bg-[#0d1117] border border-gray-800 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-teal-500/10 border-b border-gray-800 px-6 py-4">
        <div className="flex items-center gap-3">
          <Volume2 className="w-5 h-5 text-teal-400" />
          <div>
            <h2 className="text-lg font-semibold text-white">Ambient Sound</h2>
            <p className="text-gray-400 text-sm">Add background music or noise to calls</p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <p className="text-gray-400 text-sm mb-6 flex items-center gap-2">
          <span className="w-4 h-4 rounded-full border border-gray-500 inline-flex items-center justify-center text-xs">i</span>
          Enable background audio to play ambient sounds during calls. When enabled, you can select from various background audio options to enhance call quality and reduce silence.
        </p>

        {/* Enable Toggle */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <span className="text-white font-medium">Enable Background Audio</span>
            <p className="text-gray-500 text-sm">Play background audio during calls to enhance the conversation experience</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-sm">Enabled</span>
            <div 
              className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${agent.ambientSound?.enabled ? 'bg-teal-400' : 'bg-gray-700'}`}
              onClick={() => setAgent(prev => ({ 
                ...prev, 
                ambientSound: { ...prev.ambientSound, enabled: !prev.ambientSound?.enabled }
              }))}
            >
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${agent.ambientSound?.enabled ? 'translate-x-5' : ''}`} />
            </div>
          </div>
        </div>

        {agent.ambientSound?.enabled && (
          <div className="space-y-6">
            {/* Volume Control */}
            <div>
              <h3 className="text-white font-medium mb-4">Background Audio Settings</h3>
              <label className="block text-gray-300 text-sm mb-3">Background Audio Volume</label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={agent.ambientSound?.volume || 0.2}
                  onChange={(e) => setAgent(prev => ({ 
                    ...prev, 
                    ambientSound: { ...prev.ambientSound, volume: parseFloat(e.target.value) }
                  }))}
                  className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-400"
                />
                <span className="text-white font-mono text-sm w-12">{(agent.ambientSound?.volume || 0.2).toFixed(2)}</span>
              </div>
            </div>

            {/* Audio Selection */}
            <div>
              <label className="block text-gray-300 text-sm mb-3">Select Background Audio</label>
              <div className="grid grid-cols-2 gap-4">
                {backgroundAudioOptions.map((audio) => (
                  <div
                    key={audio.id}
                    onClick={() => setAgent(prev => ({ 
                      ...prev, 
                      ambientSound: { ...prev.ambientSound, selectedAudio: audio.id }
                    }))}
                    className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                      agent.ambientSound?.selectedAudio === audio.id
                        ? 'bg-teal-500/10 border-teal-500'
                        : 'bg-[#1a1a1f] border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full border-2 ${
                        agent.ambientSound?.selectedAudio === audio.id 
                          ? 'border-teal-400 bg-teal-400' 
                          : 'border-gray-500'
                      }`} />
                      <div>
                        <p className="text-white font-medium">{audio.name}</p>
                        <p className="text-gray-500 text-sm">{audio.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleAudioPreview(audio.id);
                      }}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      {playingAudio === audio.id ? (
                        <Pause className="w-5 h-5 text-teal-400" />
                      ) : (
                        <Play className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CallConfigTab;
